import styled from '@emotion/styled';
import { memoize } from 'lodash';
import type { ForwardedRef } from 'react';
import React, { forwardRef } from 'react';
import type { FieldError, FieldPath } from 'react-hook-form';
import { Controller } from 'react-hook-form';
import InputMask from 'react-input-mask';
import { ThemeColors } from '@theme/foundations/colors';
import fontSizes from '@theme/foundations/fontSizes';

import FormGroup from './FormGroup';
import type { ControlProps, FieldValues, TRules } from './types';
import { Flex, Input, Textarea } from '@chakra-ui/react';

export type TextInputProps<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>
> = {
  label: string;
  type?: 'text' | 'number' | 'password' | 'textarea' | 'maskedtext';
  mask?: string;
  disabled?: boolean;
  onBlur?: (e: React.FocusEvent<HTMLInputElement>) => void;
} & ControlProps<TFieldValues, TName>;

type InputTypeProps = {
  mask: string;
  type: Required<TextInputProps>['type'];
  maxLength: number;
} & Pick<TextInputProps, 'disabled'> & { id: string };

const InputType = forwardRef(
  (
    // eslint-disable-next-line @typescript-eslint/no-explicit-any, react/prop-types
    { type, mask, maxLength, ...props }: InputTypeProps,
    ref: ForwardedRef<any>
  ) =>
    ({
      text: (
        <Input
          {...props}
          maxLength={maxLength}
          ref={ref}
          variant="unstyled"
          onFocus={(e) => {
            const valLength = e.target.value.length;
            e.target.setSelectionRange(valLength, valLength);
            e.target.focus();
          }}
        />
      ),
      textarea: (
        <Textarea
          {...props}
          maxLength={maxLength}
          ref={ref}
          variant="unstyled"
          onFocus={(e) => {
            const valLength = e.target.value.length;
            e.target.setSelectionRange(valLength, valLength);
            e.target.focus();
          }}
        />
      ),
      number: (
        <Input
          {...props}
          maxLength={maxLength}
          ref={ref}
          type="number"
          variant="unstyled"
          onFocus={(e) => {
            const valLength = e.target.value.length;
            e.target.setSelectionRange(valLength, valLength);
            e.target.focus();
          }}
        />
      ),
      password: (
        <Input
          {...props}
          maxLength={maxLength}
          ref={ref}
          type="password"
          variant="unstyled"
          onFocus={(e) => {
            const valLength = e.target.value.length;
            e.target.setSelectionRange(valLength, valLength);
            e.target.focus();
          }}
        />
      ),
      maskedtext: (
        <InputMask
          mask={mask}
          {...props}
          onFocus={(e) => {
            const valLength = e.target.value.length;
            e.target.setSelectionRange(valLength, valLength);
            e.target.focus();
          }}
        >
          {(inputProps) => (
            <Input {...inputProps} variant="unstyled" ref={ref} />
          )}
        </InputMask>
      ),
    }[type])
);

const withDefaultRules = memoize((rules: TRules = {}) => ({
  maxLength: 250,
  ...rules,
}));

const TextInput = ({
  label,
  name,
  type = 'text',
  mask = '111',
  rules,
  control,
  disabled = false,
  formState,
}: TextInputProps) => {
  const rulesWithDefault = withDefaultRules(rules);

  return (
    <FormGroup
      error={formState?.errors?.[name] as FieldError}
      label={label}
      name={name}
      required={!!rules?.required}
      rules={rulesWithDefault}
    >
      <Controller
        control={control}
        defaultValue=""
        name={name}
        render={({ field }) => (
          <Flex gap="1" direction="column" width="100%">
            <StyledContainer hasError={!!formState?.errors?.[name]}>
              <span
                className={formState?.errors?.[name] ? 'label-error' : 'label'}
              >
                {label}
              </span>{' '}
              {rules?.required && <span className="asterisk">*</span>}
              <InputType
                disabled={disabled}
                id={`${name}-input`}
                mask={mask}
                // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                maxLength={rulesWithDefault.maxLength! as number}
                type={type}
                {...field}
              />
            </StyledContainer>
          </Flex>
        )}
        rules={rulesWithDefault}
      />
    </FormGroup>
  );
};

const StyledContainer = styled.div<{ hasError?: boolean }>`
  position: relative;
  width: 100%;
  border: ${({ hasError }) =>
    `1px solid ${
      hasError ? ThemeColors.error[600] : ThemeColors.neutral[500]
    }`};
  background: ${ThemeColors.white[500]};
  border-radius: 8px;
  padding: 4px 16px;
  font-size: ${fontSizes['md']};
  font-weight: 500;

  .label {
    color: ${ThemeColors.neutral[700]};
  }

  .label-error {
    color: ${ThemeColors.error[600]};
  }

  .asterisk {
    color: ${ThemeColors.error[500]};
  }

  input {
    margin-top: 4px;
  }
`;

export default styled(TextInput)() as unknown as typeof TextInput;
